import { TextButton } from '@creditornot/cb-button';
import { CaretLeft, CaretRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { ActiveDeliverySettings, DeliverySettingsType } from 'apis/delivery-settings-api/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { typographyCss } from 'common/typography';

import DeliveryProviderModal from './DeliveryProviderModal';
import { telemetryRestaurantDetailsInteractionClickSwitchDeliveryType } from '../telemetry';

type Props = {
  className?: string;
  venue: Venue;
  activeDeliverySettings: ActiveDeliverySettings;
  onActiveDeliveryTypeChange: () => void;
};

const DELIVERY_PROVIDERS: { [key in DeliverySettingsType]: string } = {
  wolt_delivery: 'venue-details.delivery-settings.wolt-delivery',
  self_delivery: 'venue-details.delivery-settings.self-delivery',
};

const Root = styled.div`
  margin-block-end: 32px;
  padding-block-end: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Heading = styled.h6`
  ${typographyCss.Heading6}
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  margin-block-start: 0;
  margin-block-end: 24px;
`;

const DeliveryType = styled.div`
  ${typographyCss.Body3};
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const StyledTextButton = styled(TextButton)`
  white-space: normal;
  text-align: start;
`;

const DeliverySettings = ({ className, venue, activeDeliverySettings, onActiveDeliveryTypeChange }: Props) => {
  const { cbDirection } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!activeDeliverySettings.isFlexibleFulfillmentEnabled) {
    return null;
  }

  const currentDeliveryProvider = activeDeliverySettings.activeDeliverySettingsType;

  return (
    <Root className={className}>
      <Heading>
        <FormattedMessage id="venue-details.delivery-settings.title" />
      </Heading>
      <DeliveryType>
        <span>
          <FormattedMessage id="venue-details.delivery-settings.delivery-type" />
        </span>
        <StyledTextButton
          size="small"
          icon={cbDirection === 'rtl' ? <CaretLeft /> : <CaretRight />}
          iconPosition="right"
          onClick={() => {
            telemetryRestaurantDetailsInteractionClickSwitchDeliveryType();
            setIsModalOpen(true);
          }}
        >
          <FormattedMessage id={DELIVERY_PROVIDERS[currentDeliveryProvider]} />
        </StyledTextButton>
        {isModalOpen && (
          <DeliveryProviderModal
            originView="restaurant_details"
            venue={venue}
            currentDeliveryProvider={currentDeliveryProvider}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => {
              onActiveDeliveryTypeChange();
              setIsModalOpen(false);
            }}
          />
        )}
      </DeliveryType>
    </Root>
  );
};

export default DeliverySettings;
